import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';


const ProjectDetailContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2.75rem;
  padding: 2.25rem 1.5rem 1.75rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  color: rgb(var(--fg));
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(156, 131, 224, 0.15), transparent 45%),
      radial-gradient(circle at 82% 22%, rgba(156, 131, 224, 0.08), transparent 35%);
    pointer-events: none;
    border-radius: 22px;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 480px) {
    margin-bottom: 2rem;
    padding: 1.75rem 1rem 1.35rem;
    border-radius: 18px;
  }
`;

const HeaderTextBlock = styled.div`
  max-width: 860px;
  margin: 0 auto 1.5rem;
`;

const HeaderIntroCard = styled.div`
  max-width: 920px;
  margin: 0 auto 2rem;
  padding: 1.1rem 1.25rem;
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgb(var(--fg) / 0.08);
  border-radius: 18px;
  box-shadow: 0 10px 24px rgb(0 0 0 / calc(0.35 * var(--shadow-mul)));
  backdrop-filter: blur(12px);

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 16px;
    margin-bottom: 1.5rem;
  }
`;


const Title = styled.h1`
  font-size: 2.7rem;
  margin-bottom: 0.4rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
  text-align: center;
  color: rgb(var(--fg) / 0.95);

  @media (max-width: 768px) {
    font-size: 2.15rem;
  }

  @media (max-width: 480px) {
    font-size: 1.85rem;
  }
`;

const SubTitle = styled.p`
  font-size: 1.05rem;
  margin: 0.35rem 0 0.8rem 0;
  text-align: center;
  color: rgb(var(--fg) / 0.78);
  line-height: 1.6;

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const Date = styled.p`
  font-size: 0.9rem;
  text-align: center;
  color: rgb(var(--gray) / 0.7);
  letter-spacing: 0.02em;

  @media (max-width: 768px) {
    font-size: 0.82rem;
  }
`;

const TagsContainer = styled.div`
  margin-top: 0;
  display: flex;
  justify-content: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 0;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  padding: 0.48rem 0.95rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid rgba(156, 131, 224, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: rgb(var(--fg) / 0.08);
    color: rgb(var(--fg));
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }

  @media (max-width: 768px) {
    font-size: 0.72rem;
  }
`;

const Image = styled.img`
  display: block;
  max-width: 820px;
  max-height: 560px;
  width: auto;
  height: auto;
  object-fit: contain;
  margin: 0 auto 2.5rem auto;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  background: rgb(var(--fg) / 0.04);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s;

  @media (max-width: 768px) {
    max-width: 92%;
    max-height: 420px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    max-height: 320px;
    border-radius: 14px;
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const ImageComparisonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 5rem;
  position: relative;
  flex-wrap: wrap;
  margin-bottom: 2.5rem;
  padding: 2rem 1.5rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.03);
  border: 1px solid rgb(var(--fg) / 0.06);
  box-shadow: inset 0 1px 0 rgb(var(--fg) / 0.05);

  &::before {
    content: "";
    display: block;
    position: absolute;
    top: 2rem;
    bottom: 2rem;
    left: 50%;
    width: 1px;
    background: linear-gradient(180deg, rgba(156, 131, 224, 0), rgba(156, 131, 224, 0.25), rgba(156, 131, 224, 0));
    transform: translateX(-50%);
    z-index: 1;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    padding: 1.25rem 1rem;

    &::before {
      display: none;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 1.75rem;
  padding: 1.6rem 1.45rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
    padding: 1.2rem 1rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.45rem;
  color: #9c83e0;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(156, 131, 224, 0.25);
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.18rem;
    padding-bottom: 0.6rem;
  }
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin-bottom: 1rem;

  &.caption {
    display: block;
    max-width: 100%;
    width: auto;
    text-align: center;
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    font-size: 0.96rem;
  }

  @media (max-width: 480px) {
    font-size: 0.92rem;
    line-height: 1.75;
  }
`;

const List = styled.ul`
  padding-left: 1.35rem;
  list-style-type: disc;
  color: rgb(var(--fg) / 0.82);
  margin: 0.5rem 0 0;

  @media (max-width: 480px) {
    padding-left: 1.1rem;
  }
`;

const ListItem = styled.li`
  margin-bottom: 0.72rem;
  line-height: 1.7;
  padding-left: 0.1rem;

  &::marker {
    color: #9c83e0;
  }

  @media (max-width: 768px) {
    margin-bottom: 0.55rem;
  }
`;

const CodeBlock = styled.pre`
  background: rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  border-radius: 16px;
  padding: 1.15rem;
  overflow: auto;
  max-height: 520px;
  font-family: 'Source Code Pro', monospace;
  font-size: 0.88rem;
  line-height: 1.65;
  color: rgb(var(--fg) / 0.86);
  margin: 1rem 0;
  border: 1px solid rgb(var(--fg) / 0.06);
  box-shadow: 0 16px 36px rgb(0 0 0 / calc(0.35 * var(--shadow-mul)));

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  &::-webkit-scrollbar-track {
    background: rgb(var(--fg) / 0.03);
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(156, 131, 224, 0.35);
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 131, 224, 0.55);
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.95rem;
    border-radius: 14px;
    max-height: 420px;
  }
`;

const PlaceholderImage = styled.div`
  width: 100%;
  max-width: 800px;
  height: 400px;
  background: rgb(var(--fg) / 0.035);
  border: 1px dashed rgba(156, 131, 224, 0.3);
  border-radius: 18px;
  margin: 0 auto 2.5rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--gray) / 0.7);
  font-size: 1.05rem;
  font-style: italic;
  box-shadow: 0 14px 28px rgb(0 0 0 / calc(0.35 * var(--shadow-mul)));

  @media (max-width: 768px) {
    max-width: 92%;
    height: 300px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    height: 240px;
    border-radius: 14px;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem auto 0;
  padding: 0.9rem 2rem;
  min-width: 200px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(156, 131, 224, 0.5);
  }

  @media (max-width: 768px) {
    padding: 0.85rem 1.5rem;
    font-size: 0.78rem;
  }

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
    padding: 0.8rem 1.2rem;
    font-size: 0.75rem;
  }
`;

const ScannerApp = () => {
  const tags = ['Android', 'Python', 'SQL', 'Automation'];
  const datePublished = '2025-10-08';
  const navigate = useNavigate();


  const pythonScript = `#!/usr/bin/env python3
# -*- coding: utf-8 -*-
"""
Export ERP -> products.json and push to GitHub, only if data changed.

Run once (default) or run in a loop every N minutes within a time window.
Examples:
  python query_and_launch.py
  python query_and_launch.py --loop --start 18:00 --end 23:00 --interval 5
"""

import argparse
import json
import pyodbc
import subprocess
import sys
import time
from datetime import datetime, date, time as dtime, timedelta
from hashlib import sha256
from pathlib import Path
import os

# -------------------------------------------------------------------
# 1) CONFIGURATION (secrets redacted for the public portfolio)
#    In production, move these to environment variables or a secure vault.
# -------------------------------------------------------------------
SQL_SERVER   = "REDACTED_SQL_SERVER"
SQL_DATABASE = "REDACTED_DATABASE"
SQL_USER     = "REDACTED_USER"
SQL_PASSWORD = "REDACTED_PASSWORD"

# Path to the GitHub repo that holds products.json
REPO_PATH = Path(r"C:\\path\\to\\epbooks_scanner_repo")

# Absolute path to git.exe (check with "where git" on Windows)
GIT_EXE = r"C:\\Program Files\\Git\\cmd\\git.exe"

OUTPUT_FILE = REPO_PATH / "products.json"
LOCK_FILE   = REPO_PATH / ".products_updater.lock"

# The operational SQL query is redacted. In production it returns the
# current catalog for the virtual warehouse used during the fair along
# with a live stock balance per title (opening balances + movements).
QUERY = r"""
-- SQL QUERY REDACTED FOR CONFIDENTIALITY
SELECT 1 AS DUMMY;
"""

# -------------------------------------------------------------------
# Helpers
# -------------------------------------------------------------------
def log(msg: str) -> None:
    ts = datetime.now().strftime("%Y-%m-%d %H:%M:%S")
    print(f"[{ts}] {msg}", flush=True)

def run_once() -> bool:
    """
    Run query, write products.json if changed, and push.
    Returns True if a push occurred, else False.
    """
    rows = export_rows()
    if write_if_changed(rows):
        git_push()
        return True
    return False

def acquire_lock() -> None:
    """Simple single-instance lock using an exclusive file create."""
    try:
        LOCK_FILE.parent.mkdir(parents=True, exist_ok=True)
        with open(LOCK_FILE, "x") as f:  # fails if file exists
            f.write(str(os.getpid()))
    except FileExistsError:
        log("Another instance seems to be running (lock file exists). Exiting.")
        sys.exit(0)

def release_lock() -> None:
    try:
        LOCK_FILE.unlink(missing_ok=True)
    except Exception:
        pass

def parse_hhmm(s: str) -> dtime:
    return datetime.strptime(s, "%H:%M").time()

def is_within_window(now: datetime, start: dtime, end: dtime) -> bool:
    """Inclusive start, exclusive end. Assumes same-day window."""
    today_start = datetime.combine(now.date(), start)
    today_end   = datetime.combine(now.date(), end)
    return today_start <= now < today_end

def next_tick(now: datetime, interval_minutes: int) -> datetime:
    """Next aligned tick at a multiple of interval minutes (e.g., :00, :05, :10...)."""
    seconds = interval_minutes * 60
    since_hour = now.minute * 60 + now.second
    to_next = seconds - (since_hour % seconds)
    if to_next == 0:
        to_next = seconds
    return now + timedelta(seconds=to_next)

def sleep_until(dt: datetime) -> None:
    secs = max(1, int((dt - datetime.now()).total_seconds()))
    time.sleep(secs)

# -------------------------------------------------------------------
# 2) DATA EXPORT
# -------------------------------------------------------------------
def export_rows():
    """Run the SQL query and return a list[dict] of rows."""
    log("Running query ...")
    conn = pyodbc.connect(
        f"DRIVER={{ODBC Driver 17 for SQL Server}};"
        f"SERVER={SQL_SERVER};DATABASE={SQL_DATABASE};UID={SQL_USER};PWD={SQL_PASSWORD}"
    )
    try:
        cur = conn.cursor()
        cur.execute(QUERY)
        columns = [c[0] for c in cur.description]
        rows = [dict(zip(columns, r)) for r in cur.fetchall()]
        log(f"Query returned {len(rows)} rows.")
        return rows
    finally:
        conn.close()

# -------------------------------------------------------------------
# 3) CHANGE DETECTION (stable ordering + hashing)
# -------------------------------------------------------------------
def _read_existing_products():
    if OUTPUT_FILE.exists():
        try:
            data = json.loads(OUTPUT_FILE.read_text(encoding="utf-8"))
            if isinstance(data, dict) and "last_updated" in data and "products" in data:
                return data["products"]
            if isinstance(data, list):
                return data
        except Exception:
            pass
    return []

def _stable_products(rows):
    """Deterministic ordering for comparison and commits."""
    return sorted(
        rows,
        key=lambda r: (str(r.get("ΚΩΔΙΚΟΣ", "")), str(r.get("ISBN", "")))
    )

def _hash(products):
    dumped = json.dumps(products, ensure_ascii=False, sort_keys=True, separators=(",", ":"))
    return sha256(dumped.encode("utf-8")).hexdigest()

def write_if_changed(new_rows):
    """
    Write products.json (with last_updated) only if the products changed.
    Returns True if a change was detected.
    """
    old = _stable_products(_read_existing_products())
    newp = _stable_products(new_rows)

    if _hash(old) == _hash(newp):
        log("No data change; skipping write/commit.")
        return False

    payload = {
        "last_updated": datetime.now().strftime("%Y-%m-%d %H:%M"),
        "products": newp,
    }
    OUTPUT_FILE.parent.mkdir(parents=True, exist_ok=True)
    OUTPUT_FILE.write_text(json.dumps(payload, ensure_ascii=False), encoding="utf-8")
    log(f"Wrote {len(newp)} products to {OUTPUT_FILE}")
    return True

# -------------------------------------------------------------------
# 4) GIT COMMIT/PUSH
# -------------------------------------------------------------------
def git_push():
    """Stage, commit, and push products.json to GitHub."""
    ts = datetime.now().strftime("%Y-%m-%d %H:%M")
    cmds = [
        [GIT_EXE, "add", "products.json"],
        [GIT_EXE, "commit", "-m", f"Auto update {ts}"],
        [GIT_EXE, "push", "origin", "main"],
    ]
    for cmd in cmds:
        subprocess.run(cmd, cwd=REPO_PATH, check=True)
    log("Pushed latest products.json to GitHub.")

# -------------------------------------------------------------------
# 5) LOOP DRIVER
# -------------------------------------------------------------------
def run_loop(start_str: str, end_str: str, interval_minutes: int):
    """Run every N minutes within daily window [start, end)."""
    acquire_lock()
    log(f"Entering loop: {start_str}–{end_str} every {interval_minutes} min")
    try:
        start_t = parse_hhmm(start_str)
        end_t   = parse_hhmm(end_str)

        while True:
            now = datetime.now()
            if is_within_window(now, start_t, end_t):
                try:
                    pushed = run_once()
                    if pushed:
                        log("Change detected and pushed.")
                    else:
                        log("No change.")
                except Exception as e:
                    log(f"Run error: {e}")

                nxt = next_tick(datetime.now(), interval_minutes)
                if not is_within_window(nxt, start_t, end_t):
                    tomorrow = datetime.combine(date.today() + timedelta(days=1), start_t)
                    log(f"Window ended. Sleeping until {tomorrow.strftime('%Y-%m-%d %H:%M')}")
                    sleep_until(tomorrow)
                else:
                    sleep_until(nxt)
            else:
                today_start = datetime.combine(date.today(), start_t)
                if now < today_start:
                    target = today_start
                else:
                    target = datetime.combine(date.today() + timedelta(days=1), start_t)
                log(f"Outside window. Sleeping until {target.strftime('%Y-%m-%d %H:%M')}")
                sleep_until(target)
    finally:
        release_lock()
        log("Loop exited; lock released.")

# -------------------------------------------------------------------
# 6) CLI
# -------------------------------------------------------------------
def main():
    parser = argparse.ArgumentParser(description="Export ERP data to products.json and push if changed.")
    parser.add_argument("--loop", action="store_true", help="Run forever within a daily window.")
    parser.add_argument("--start", default="18:00", help="Daily window start (HH:MM). Default 18:00")
    parser.add_argument("--end",   default="23:00", help="Daily window end (HH:MM, exclusive). Default 23:00")
    parser.add_argument("--interval", type=int, default=5, help="Minutes between runs inside the window. Default 5")
    args = parser.parse_args()

    REPO_PATH.mkdir(parents=True, exist_ok=True)
    os.chdir(REPO_PATH)

    if args.loop:
        run_loop(args.start, args.end, args.interval)
    else:
        try:
            pushed = run_once()
            if pushed:
                log("Change detected and pushed.")
            else:
                log("No change.")
        except Exception as e:
            log(f"Failure: {e}")
        finally:
            log("Done.")

if __name__ == "__main__":
    main()
`;

  return (
    <ProjectDetailContainer>
      <Header>
        <HeaderTextBlock>
          <Title>ISBN Scanner App</Title>
          <SubTitle>Built for the 53rd Athens Book Fair (Πεδίον του Άρεως)</SubTitle>
          <Date>Published on: {datePublished}</Date>
        </HeaderTextBlock>

        <CoverImage src="/images/scanner_app_icon.png" alt="Book Fair Price Scanner Cover" />

        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
      </Header>

      <Section>
        <SectionTitle>Context & Problem</SectionTitle>
        <Text>
          This was fundamentally an operational throughput problem rooted in poor information accessibility.
        </Text>
        <Text>
          During the 53rd Athens Book Fair, attendants needed to respond quickly to price and stock inquiries. Around 100 titles were physically available at the booth, while a larger catalog of 3,500+ titles was searchable to verify whether a book existed in our broader inventory and could be brought in through special orders. Without price stickers (to avoid damaging covers), staff relied on a printed six‑page price list. Even though it was alphabetically sorted, picking it up and manually scanning for a title took around 10 seconds per lookup—slowing interactions, looking less professional, and creating noticeable delays during peak hours.
          The core issue was not just speed — it was the absence of structured, accessible data to support decision-making during live operations.
        </Text>
        <Image src="/images/book_fair.png" alt="Book Fair Booth Setup" />
        <Text style={{
          fontStyle: 'italic',
          textAlign: 'center',
          marginTop: '0.9rem',
          marginBottom: '2rem',
          color: 'rgb(var(--gray) / 0.7)',
          background: 'rgb(var(--fg) / 0.04)',
          border: '1px solid rgb(var(--fg) / 0.06)',
          maxWidth: '760px',
          padding: '0.6rem 0.9rem',
          borderRadius: '12px',
          marginLeft: 'auto',
          marginRight: 'auto',
          fontSize: '0.9rem'
        }}>
          This created a clear operational bottleneck: the manual list slowed attendants, delayed customers, and limited our ability to handle demand efficiently.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Solution</SectionTitle>
        <Text>
          The challenge wasn’t just building a tool — it was designing a reliable information flow under security, hardware, and time constraints.
        </Text>
        <Text>
          On the first day, I attempted a quick Excel‑based workaround on a single laptop. It wasn’t a simple sheet: the catalog was loaded into a structured table, and I built a separate ‘lookup portal’ that used XLOOKUP formulas. The barcode scanner (acting as a keyboard wedge) automatically dropped the ISBN into a search field, instantly returning details in the adjacent fields. While faster than the printed list, it still created a central bottleneck—only one attendant could use the station while also processing sales.
        </Text>
        <Text>
          To decentralize operations, I designed and implemented a lightweight Android price‑scanner app paired with a simple data‑sync pipeline. Direct on‑device ERP access was non‑viable: our ERP is strictly on‑premise, and the only secure connection available was the VPN‑enabled POS laptop. Exposing SQL directly to mobile devices—even internally—would have required insecure workarounds and risked leaking sensitive data. To avoid this, I built a controlled Python exporter that extracted only approved fields (book details + stock) and generated a stable products.json file. No sales, pricing history, or sensitive ERP data ever left the secure environment.
          The solution had to operate under strict constraints: an on‑prem ERP with no external exposure, limited event‑day hardware, offline‑first requirements, and the need for a deterministic, failure‑proof sync process that would not disrupt operations.
        </Text>
        <Text>
          The entire system was designed, architected, and deployed under real event constraints—prioritizing correctness, speed, and resilience.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Data Flow</SectionTitle>
        <Text>
          The system synchronized approximately 3,500 SKUs every 5 minutes, reducing real lookup time from ~8–10 seconds with the printed list (often longer when the list wasn’t already in hand) to ~1 second through the app.
        </Text>
        <List>
          <ListItem>ERP (on‑prem) → Python exporter executes every 5 minutes during fair hours.</ListItem>
          <ListItem>Exporter performs deterministic sorting + hashing; writes products.json only when data changes.</ListItem>
          <ListItem>Android app fetches the JSON; it compares the embedded timestamp to avoid unnecessary downloads and only refreshes the local cache when data truly changes.</ListItem>
          <ListItem>Privacy preserved: only catalog + stock levels are shared—no transactional data.</ListItem>
        </List>
        <Text style={{ fontWeight: '600', marginTop: '1rem' }}>Analytics Relevance</Text>
        <Text>
          Each scan created a structured “interest signal,” enabling metrics such as demand-per-title, scan-to-sale ratios, peak inquiry periods, and category-level heatmaps. These signals formed the basis for forecasting, booth optimization, and post-event analytics — insights that were previously impossible.
        </Text>
      </Section>

      <Section>
        <SectionTitle>App Features</SectionTitle>
        <Text>
          Designed for real‑time performance in a high‑traffic environment, optimized for reliability and offline‑first use.
        </Text>
        <List>
          <ListItem><strong>Home:</strong> Last‑sync timestamp, lifetime scans, most‑scanned item, and a category breakdown.</ListItem>
          <ListItem><strong>Two primary actions:</strong> barcode Scan and Manual Search (title, internal code, ISBN keypad).</ListItem>
          <ListItem><strong>Result page:</strong> Title, author, series/category, audience range, class level, and live stock.</ListItem>
          <ListItem><strong>History tools:</strong> Export scans to CSV and clear history for post‑event analysis.</ListItem>
          <ListItem><strong>Offline‑first architecture:</strong> full functionality from cached catalog; pull‑to‑refresh for updates.</ListItem>
        </List>
        <div style={{ marginTop: '2.5rem' }}>
          <div style={{ marginTop: '0' }}>
            <ImageComparisonContainer>
              {/* Left Image */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  maxWidth: '350px',
                  width: '100%',
                }}
              >
                <img
                  src="/images/scanner_app_home.png"
                  alt="Home Screen – Live Sync, KPIs, and Category Breakdown"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '18px',
                    border: '1px solid rgb(var(--fg) / 0.08)',
                    background: 'rgb(var(--fg) / 0.04)',
                    boxShadow: '0 16px 34px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)))',
                    filter: 'brightness(0.92) saturate(0.95)',
                  }}
                />
                <p
                  style={{
                    fontStyle: 'italic',
                    textAlign: 'center',
                    color: 'rgb(var(--gray) / 0.7)',
                    marginTop: '0.9rem',
                    width: '100%',
                    maxWidth: '350px',
                    lineHeight: '1.5',
                    padding: '0.6rem 0.75rem',
                    background: 'rgb(var(--fg) / 0.04)',
                    border: '1px solid rgb(var(--fg) / 0.06)',
                    borderRadius: '12px',
                    fontSize: '0.9rem',
                  }}
                >
                  Home Screen – Live Sync, KPIs, and Category Breakdown
                </p>
              </div>

              {/* Right Image */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  maxWidth: '350px',
                  width: '100%',
                }}
              >
                <img
                  src="/images/scanner_app_manual_lookup.png"
                  alt="Manual Lookup – Search by Title, ISBN, or Internal Code"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '18px',
                    border: '1px solid rgb(var(--fg) / 0.08)',
                    background: 'rgb(var(--fg) / 0.04)',
                    boxShadow: '0 16px 34px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)))',
                    filter: 'brightness(0.92) saturate(0.95)',
                  }}
                />
                <p
                  style={{
                    fontStyle: 'italic',
                    textAlign: 'center',
                    color: 'rgb(var(--gray) / 0.7)',
                    marginTop: '0.9rem',
                    width: '100%',
                    maxWidth: '350px',
                    lineHeight: '1.5',
                    padding: '0.6rem 0.75rem',
                    background: 'rgb(var(--fg) / 0.04)',
                    border: '1px solid rgb(var(--fg) / 0.06)',
                    borderRadius: '12px',
                    fontSize: '0.9rem',
                  }}
                >
                  Manual Lookup – Search by Title, ISBN, or Internal Code
                </p>
              </div>
            </ImageComparisonContainer>
          </div>
        </div>
      </Section>

      <Section>
        <SectionTitle>Architecture & Tech</SectionTitle>
        <Text>
          A modular, offline‑first architecture combining mobile UI, a deterministic data‑sync engine, lightweight distribution, and read‑only access to the ERP. The design prioritized operational reliability, minimal dependencies, and fast iteration under event‑day pressure.
        </Text>
        <List>
          <ListItem>Android app: barcode scanning, offline cache, incremental refresh.</ListItem>
          <ListItem>Python exporter: generates {`{ last_updated, products: [...] }`} with stable ordering + hashing.</ListItem>
          <ListItem>SQL Server (SoftOne ERP): read‑only extraction of catalog + stock.</ListItem>
          <ListItem>Distribution through a universal APK for attendants’ existing devices.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Python Sync Script (redacted)</SectionTitle>
        <Text>
          The script below queries the ERP for the fair’s virtual warehouse and writes a <code>products.json</code> file containing the catalog and live stock. If the products haven’t changed since the last run, it skips the commit to avoid noisy pushes.
        </Text>
        <CodeBlock>{pythonScript}</CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Impact</SectionTitle>
        <Text>
          <strong>Before: </strong>
          Before the app, attendants worked with a six‑page printed list or queued for the single Excel lookup station. Lookups took ~10 seconds, disrupted the flow of conversation, and repeatedly created bottlenecks when multiple customers asked for information simultaneously.
        </Text>
        <Text>
          <strong>After: </strong>
          With decentralized, instant lookup on every device, attendants no longer waited for access to a single laptop. Answers became immediate, interactions were smoother, and staff visibility across the booth improved.
        </Text>
        <Text>
          <strong>Efficiency Gains: </strong>
          Lookup time dropped from ~10 seconds to under 1 second. Attendants processed more customers per hour, queue formation dropped sharply, and overall service speed improved noticeably—especially during peak periods.
        </Text>
        <Text>
          <strong>Reliability &amp; Quality: </strong>
          The cached‑offline model ensured the app worked even during poor network conditions, the timestamped JSON prevented redundant downloads, and the deterministic sync process guaranteed consistent data across all devices.
        </Text>
        <Text>
          <strong>Business Value: </strong>
          Sales ended more than 10% higher year‑over‑year, supported in part by faster throughput, higher customer satisfaction, and the foundation for structured ‘interest‑signal’ data that can guide future inventory and marketing decisions.
        </Text>
        <Text>
          This project demonstrated how small operational tools can produce clean, high‑resolution behavioral datasets — exactly the type of data foundation required in modern Business Analytics.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Future Outlook</SectionTitle>
        <Text>
          Next, the system can evolve from a lookup tool into a lightweight Store Assistant. Each scan becomes an interest signal—capturing timestamp, product identifiers, and staff activity—creating a clean dataset for operational analytics.
        </Text>
        <Text>
          Planned additions include quick‑basket handling, stocktake tools, low‑stock alerts, and reorder recommendations. Multi‑device sync and role‑based logic would support coordinated booth or store operations.
        </Text>
        <Text>
          The Greek book retail environment is particularly well‑suited for this design: ISBNs provide a universal identifier, events produce high lookup volumes, and sticker‑less pricing creates consistent need for fast digital lookup.
        </Text>
        <Text>
          Longer term, the framework can support pop‑ups, specialty retail, and exhibitions—offering retailers and publishers anonymized demand insights that traditional POS systems cannot capture.
        </Text>
      </Section>

      <Section>
        <SectionTitle>What I Learned</SectionTitle>
        <Text>
          Delivering under real operational pressure required disciplined scope reduction, reliability‑first design, and an understanding of how data flows shape real work. By addressing the data pipeline, the mobile interface, and the operational workflow together, I saw how well‑structured information flows translate directly into faster decisions and measurable outcomes.
        </Text>
      </Section>

      <Button onClick={() => navigate('/projects')}>Back to Projects</Button>
    </ProjectDetailContainer>
  );
};

// Export metadata for use in the Projects page
export const ScannerAppMetadata = {
  title: 'ISBN Scanner App',
  description: 'Offline-first Android scanner powered by a Python-driven ERP exporter that syncs a live catalog and stock levels to a deterministic JSON feed, enabling instant price lookup and decentralized event-day operations.',
  tags: ['Android', 'Python', 'SQL', 'Automation'],
  languages: ['Python', 'SQL Server', 'GitHub', 'Android'],
  datePublished: '2025-10-08',
  path: '/projects/scanner-app',
  coverImage: '/images/scanner_app_icon.png'
};

export default ScannerApp;